// src/client/composables/useAuth.js
console.log("useAuth module loaded", Math.random());

import { ref } from "vue";
import router from "@/router";

import {
  initIdleTimer,
  resetIdleTimer,
  stopIdleTimer
} from "@/utils/idleTimer.js";

import { registerLogoutHandler } from "@/utils/api.js";
import { sysConfig } from "~shared/config/system.cfg.js";

/* ============================================================
   STATE
============================================================ */
const user = ref(null);
const accessToken = ref(localStorage.getItem("access_token") || null);
const loading = ref(false);

/* ============================================================
   TOKEN NORMALIZATION
   Backend always returns shape:
      { accessToken, expiresIn }
============================================================ */
function normalizeToken(data) {
  const token = data?.accessToken || null;

  const expiresRaw = Number(data?.expiresIn);
  const expiresIn =
    expiresRaw > 0 ? expiresRaw : (sysConfig.tokenExpirySeconds || 900);

  return { token, expiresIn };
}

/* ============================================================
   SAFE LOGOUT
============================================================ */
async function safeLogout() {
  stopIdleTimer();

  accessToken.value = null;
  user.value = null;
  localStorage.removeItem("access_token");

  if (router.currentRoute.value.path !== "/login") {
    router.push("/login");
  }
}

export const logout = async () => {
  try {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });
  } catch {}

  await safeLogout();
};

registerLogoutHandler(logout);

/* ============================================================
   AUTH STATE CHECK
============================================================ */
export const isAuthenticated = () =>
  !!accessToken.value && !!user.value;

/* ============================================================
   LOGIN
   NOTE: idleTimer MUST NOT start here
============================================================ */
export const login = async (email, password) => {
  loading.value = true;

  try {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Login failed");

    const { token } = normalizeToken(data);
    if (!token) throw new Error("Backend returned no token");

    accessToken.value = token;
    localStorage.setItem("access_token", token);

    console.log("LOGIN TOKEN STORED:", token);

    // Fetch user profile, start idle timer afterwards
    await fetchUser();
    return true;

  } catch (err) {
    console.error("LOGIN ERROR:", err);
    throw err;

  } finally {
    loading.value = false;
  }
};

/* ============================================================
   FETCH USER (/me)
   Starts idle timer ONLY after profile is loaded
============================================================ */
export const fetchUser = async () => {
  try {
    console.log("CALLING /api/auth/me");

    const res = await fetch("/api/auth/me", {
      credentials: "include"
    });

    console.log("fetchUser status:", res.status);

    if (res.status === 401 || res.status === 403) {
      await safeLogout();
      return false;
    }

    if (!res.ok) {
      console.warn("⚠ /me returned non-OK but not unauthorized");
      return false;
    }

    const data = await res.json();
    if (!data || typeof data !== "object") return false;

    user.value = data;

    // Start idle timer safely after successful login/refresh
    initIdleTimer(() => logout(), {
      timeoutMs: sysConfig.idleDelayMs,
      heartbeatMs: 1000,
      onProgress: pct => window.__idleProgress?.(pct),
    });

    resetIdleTimer();
    return true;

  } catch (err) {
    console.error("❌ fetchUser error:", err);
    return false;
  }
};

/* ============================================================
   SILENT REFRESH
============================================================ */
export const silentRefresh = async () => {
  try {
    const res = await fetch("/api/auth/refresh-silent", {
      credentials: "include"
    });

    if (!res.ok) return false;

    const data = await res.json();
    const { token } = normalizeToken(data);

    if (!token) return false;

    accessToken.value = token;
    localStorage.setItem("access_token", token);

    // Restart idle timer
    initIdleTimer(() => logout(), {
      timeoutMs: sysConfig.idleDelayMs,
      heartbeatMs: 1000,
      onProgress: pct => window.__idleProgress?.(pct)
    });

    // Refresh user object too
    await fetchUser();
    return true;

  } catch (err) {
    console.error("SILENT REFRESH ERROR:", err);
    return false;
  }
};

/* ============================================================
   EXPORT
============================================================ */
export const useAuth = () => ({
  user,
  accessToken,
  loading,
  isAuthenticated,
  login,
  logout,
  fetchUser,
  silentRefresh,
});
